import LoadingButton from '@mui/lab/LoadingButton';
import { Autocomplete, MenuItem } from '@mui/material';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import useTheme from '@mui/material/styles/useTheme';
import TextField from '@mui/material/TextField';
import useMediaQuery from '@mui/material/useMediaQuery';
import Box from '@mui/system/Box';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { DataItem } from '~components/DataItem';
import ControlledCheckbox from '~components/Form/ControlledCheckbox';
import ControlledNumberField from '~components/Form/ControlledNumberField';
import SectionCard from '~components/SectionCard';
import useDiallingHourProfiles from '~hooks/useDiallingHourProfiles';
import { getWashLists } from '~pages/CampaignManagement/api';
import { LeadEngineSettings, WashList } from '~pages/CampaignManagement/domain';
import { getExclusionLists } from '~pages/SystemManagement/api';
import { ExclusionList } from '~pages/SystemManagement/domain';
import { useNotification } from '~providers/NotificationProvider';
import { APIError, UnsupportedStructureError } from '~services/Errors';
import { deepCompare } from '~utils/Functions';

enum CallbackRescheduleSeconds {
  Min = 0,
  Max = 86400,
  Default = 3600,
}

enum CallbackMaxReschedules {
  Min = 1,
  Max = 240,
  Never = 0,
  Unlimited = -1,
}

interface LeadEngineSettingsFormProps {
  leadEngineSettings: LeadEngineSettings;
  isEdit: boolean;
  submitting: boolean;
  toggleEdit?: () => void;
  onSubmit: (data: LeadEngineSettings) => Promise<void>;
}

const GetLabelFromValue = <T = any,>(options: { label: string; value: T }[], value: any): string => {
  for (let option of options) {
    if (option.value === value) {
      return option.label;
    }
  }
  return 'Unknown';
};

type ListItem = {
  label: string;
  value: string;
};

interface Form {
  allowCallbackReschedule: boolean;
  callbackShiftLeadExpiry: boolean;
  duplicateSearchAllCampaigns: boolean;
  washEndpointOnly: boolean;
  allowContactedDuplication: boolean;
  sortAttemptCountFirst: boolean;
  enableEndpointSequence: boolean;
  diallingHourProfile: string;
  duplicateSearchDays: number;
  endpointAttemptIntervalSeconds: number;
  endpointMachineIntervalSeconds: number;
  endpointEngagedIntervalSeconds: number;
  endpointNoAnswerIntervalSeconds: number;
  endpointDailyMaxAttempts: number;
  externalIdAttemptIntervalSeconds: number;
  externalIdDailyMaxAttempts: number;
  leadEndpointTotalMaxAttempts: number;
  leadTotalMaxAttempts: number;
  washLists: ListItem[];
  exclusionLists: ListItem[];
  limitCallbackReschedule: boolean;
  callbackMaxReschedules: number;
  callbackRescheduleSeconds: number;
  reassignMissedCallbackToGroup: boolean;
}

const LeadEngineSettingsForm = ({
  leadEngineSettings,
  isEdit,
  submitting,
  toggleEdit,
  onSubmit,
}: LeadEngineSettingsFormProps) => {
  const theme = useTheme();
  const isExtraSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const { pushNotification } = useNotification();
  const [exclusionLists, setExclusionLists] = useState<ListItem[]>([]);
  const [washListOptions, setWashListOptions] = useState<ListItem[]>([]);
  const { list: diallingHourProfileOptions } = useDiallingHourProfiles();
  const DiallingHourProfileOptionsList = diallingHourProfileOptions.map((item, index) => (
    <MenuItem key={index} value={item.value}>
      {item.label}
    </MenuItem>
  ));

  const {
    control,
    watch,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<Form>({
    defaultValues: {
      duplicateSearchAllCampaigns: false,
      washEndpointOnly: false,
      allowContactedDuplication: false,
      diallingHourProfile: '',
      duplicateSearchDays: 0,
      endpointAttemptIntervalSeconds: 0,
      endpointMachineIntervalSeconds: 0,
      endpointEngagedIntervalSeconds: 0,
      endpointNoAnswerIntervalSeconds: 0,
      endpointDailyMaxAttempts: 0,
      externalIdAttemptIntervalSeconds: 0,
      externalIdDailyMaxAttempts: 0,
      leadEndpointTotalMaxAttempts: 0,
      leadTotalMaxAttempts: 0,
      allowCallbackReschedule: false,
      reassignMissedCallbackToGroup: false,
      callbackRescheduleSeconds: CallbackRescheduleSeconds.Default,
      limitCallbackReschedule: false,
      callbackMaxReschedules: CallbackMaxReschedules.Min,
      callbackShiftLeadExpiry: false,
      sortAttemptCountFirst: false,
      enableEndpointSequence: false,
      washLists: [],
      exclusionLists: [],
    },
    mode: 'all',
    reValidateMode: 'onChange',
    shouldUnregister: false,
  });
  const disablePerformAction =
    leadEngineSettings &&
    deepCompare(leadEngineSettings.duplicateSearchAllCampaigns, watch('duplicateSearchAllCampaigns')) &&
    deepCompare(leadEngineSettings.washEndpointOnly, watch('washEndpointOnly')) &&
    deepCompare(leadEngineSettings.allowContactedDuplication, watch('allowContactedDuplication')) &&
    deepCompare(leadEngineSettings.diallingHourProfile, watch('diallingHourProfile')) &&
    deepCompare(leadEngineSettings.duplicateSearchDays, watch('duplicateSearchDays')) &&
    deepCompare(leadEngineSettings.endpointAttemptIntervalSeconds, watch('endpointAttemptIntervalSeconds')) &&
    deepCompare(leadEngineSettings.endpointMachineIntervalSeconds, watch('endpointMachineIntervalSeconds')) &&
    deepCompare(leadEngineSettings.endpointEngagedIntervalSeconds, watch('endpointEngagedIntervalSeconds')) &&
    deepCompare(leadEngineSettings.endpointNoAnswerIntervalSeconds, watch('endpointNoAnswerIntervalSeconds')) &&
    deepCompare(leadEngineSettings.endpointDailyMaxAttempts, watch('endpointDailyMaxAttempts')) &&
    deepCompare(leadEngineSettings.externalIdAttemptIntervalSeconds, watch('externalIdAttemptIntervalSeconds')) &&
    deepCompare(leadEngineSettings.externalIdDailyMaxAttempts, watch('externalIdDailyMaxAttempts')) &&
    deepCompare(leadEngineSettings.leadEndpointTotalMaxAttempts, watch('leadEndpointTotalMaxAttempts')) &&
    deepCompare(leadEngineSettings.leadTotalMaxAttempts, watch('leadTotalMaxAttempts')) &&
    deepCompare(Boolean(leadEngineSettings.callbackMaxReschedules), watch('allowCallbackReschedule')) &&
    deepCompare(leadEngineSettings.reassignMissedCallbackToGroup, watch('reassignMissedCallbackToGroup')) &&
    deepCompare(
      leadEngineSettings.callbackRescheduleSeconds || CallbackRescheduleSeconds.Default,
      watch('callbackRescheduleSeconds'),
    ) &&
    deepCompare(
      leadEngineSettings.callbackMaxReschedules >= CallbackMaxReschedules.Min,
      watch('limitCallbackReschedule'),
    ) &&
    deepCompare(
      Math.max(leadEngineSettings.callbackMaxReschedules || 0, CallbackMaxReschedules.Min),
      watch('callbackMaxReschedules'),
    ) &&
    deepCompare(leadEngineSettings.callbackShiftLeadExpiry, watch('callbackShiftLeadExpiry')) &&
    deepCompare(leadEngineSettings.sortAttemptCountFirst, watch('sortAttemptCountFirst')) &&
    deepCompare(leadEngineSettings.enableEndpointSequence, watch('enableEndpointSequence')) &&
    deepCompare(
      leadEngineSettings.washLists.map((item) => ({ label: item, value: item })).sort(),
      watch('washLists')?.sort(),
    ) &&
    deepCompare(
      leadEngineSettings.exclusionLists.map((item) => ({ label: item, value: item })).sort(),
      watch('exclusionLists')?.sort(),
    );
  const [allowCallbackReschedule, limitCallbackReschedule] = watch([
    'allowCallbackReschedule',
    'limitCallbackReschedule',
  ]);
  const leadEngineWashLists = leadEngineSettings.washLists || [];
  const leadEngineExclusionLists = leadEngineSettings.exclusionLists || [];

  // Handles reset and editable display
  useEffect(() => {
    if (isEdit) {
      reset({
        duplicateSearchAllCampaigns: leadEngineSettings.duplicateSearchAllCampaigns,
        washEndpointOnly: leadEngineSettings.washEndpointOnly,
        allowContactedDuplication: leadEngineSettings.allowContactedDuplication,
        diallingHourProfile: leadEngineSettings.diallingHourProfile,
        duplicateSearchDays: leadEngineSettings.duplicateSearchDays,
        endpointAttemptIntervalSeconds: leadEngineSettings.endpointAttemptIntervalSeconds,
        endpointMachineIntervalSeconds: leadEngineSettings.endpointMachineIntervalSeconds,
        endpointEngagedIntervalSeconds: leadEngineSettings.endpointEngagedIntervalSeconds,
        endpointNoAnswerIntervalSeconds: leadEngineSettings.endpointNoAnswerIntervalSeconds,
        endpointDailyMaxAttempts: leadEngineSettings.endpointDailyMaxAttempts,
        externalIdAttemptIntervalSeconds: leadEngineSettings.externalIdAttemptIntervalSeconds,
        externalIdDailyMaxAttempts: leadEngineSettings.externalIdDailyMaxAttempts,
        leadEndpointTotalMaxAttempts: leadEngineSettings.leadEndpointTotalMaxAttempts,
        leadTotalMaxAttempts: leadEngineSettings.leadTotalMaxAttempts,
        allowCallbackReschedule: Boolean(leadEngineSettings.callbackMaxReschedules),
        reassignMissedCallbackToGroup: leadEngineSettings.reassignMissedCallbackToGroup,
        callbackRescheduleSeconds: leadEngineSettings.callbackRescheduleSeconds || CallbackRescheduleSeconds.Default,
        limitCallbackReschedule: leadEngineSettings.callbackMaxReschedules >= CallbackMaxReschedules.Min,
        callbackMaxReschedules: Math.max(leadEngineSettings.callbackMaxReschedules || 0, CallbackMaxReschedules.Min),
        callbackShiftLeadExpiry: leadEngineSettings.callbackShiftLeadExpiry,
        sortAttemptCountFirst: leadEngineSettings.sortAttemptCountFirst,
        enableEndpointSequence: leadEngineSettings.enableEndpointSequence,
        washLists: leadEngineSettings.washLists.map((item) => ({ label: item, value: item })),
        exclusionLists: leadEngineSettings.exclusionLists.map((item) => ({ label: item, value: item })),
      });
    }

    // Reset form on close
    return () => {
      reset();
    };
  }, [isEdit]);

  useEffect(() => {
    (async () => {
      let resp: ExclusionList[];
      try {
        resp = await getExclusionLists();
      } catch (e) {
        pushNotification('error', (e as APIError | UnsupportedStructureError).message);
        return;
      }
      setExclusionLists(resp.map((item) => ({ label: item.name, value: item.name })));
    })();
  }, []);

  useEffect(() => {
    (async () => {
      let resp: WashList[];

      try {
        resp = await getWashLists();
      } catch (e) {
        pushNotification('error', (e as APIError | UnsupportedStructureError).message);
        return;
      }

      setWashListOptions(resp);
    })();
  }, []);

  const onSubmitFn = handleSubmit((formData: any) => {
    const data: LeadEngineSettings = {
      ...formData,
      washLists: formData.washLists.map((item: ListItem) => item.value),
      exclusionLists: formData.exclusionLists.map((item: ListItem) => item.value),
    };

    if (!formData.allowCallbackReschedule) {
      data.callbackMaxReschedules = CallbackMaxReschedules.Never;
    } else if (!formData.limitCallbackReschedule) {
      data.callbackMaxReschedules = CallbackMaxReschedules.Unlimited;
    }

    onSubmit(data);
  });

  const leadEngineWashListsChips = leadEngineWashLists.map((item: string) => (
    <Chip
      key={item}
      sx={{ minWidth: 100, maxWidth: 200, margin: '4px' }}
      variant='filled'
      color='primary'
      label={GetLabelFromValue(washListOptions, item)}
    />
  ));

  const leadEngineExclusionListsChips = leadEngineExclusionLists.map((item: string) => (
    <Chip
      key={item}
      sx={{ minWidth: 100, maxWidth: 200, margin: '4px' }}
      variant='filled'
      color='primary'
      label={item}
    />
  ));

  // We do this so that if we have zero results we allow the DataItem to add '-' for an undefined or null value
  const leadEngineWashListsDisplay =
    leadEngineWashListsChips.length > 0 ? <Box sx={{ marginLeft: '-4px' }}>{leadEngineWashListsChips}</Box> : null;

  // We do this so that if we have zero results we allow the DataItem to add '-' for an undefined or null value
  const leadEngineExclusionListsDisplay =
    leadEngineExclusionListsChips.length > 0 ? (
      <Box sx={{ marginLeft: '-4px' }}>{leadEngineExclusionListsChips}</Box>
    ) : null;

  return (
    <SectionCard title='Lead Engine Settings' onEdit={toggleEdit}>
      {!isEdit && (
        <>
          <DataItem
            stacked={isExtraSmall}
            disableMargin
            tooltip='Should the campaign search for duplicate leads across all campaigns.'
            title='Duplicate search all campaigns'
            value={leadEngineSettings.duplicateSearchAllCampaigns ? 'Yes' : 'No'}
          />
          <DataItem
            stacked={isExtraSmall}
            title='Wash endpoint only'
            tooltip='Should lead endpoints be washed against an exclusion list.'
            value={leadEngineSettings.washEndpointOnly ? 'Yes' : 'No'}
          />
          <DataItem
            stacked={isExtraSmall}
            title='Allow contacted duplication'
            tooltip='Even if an uploaded lead has been attempted in the past, start dialling them again.'
            value={leadEngineSettings.allowContactedDuplication ? 'Yes' : 'No'}
          />
          <DataItem
            stacked={isExtraSmall}
            title='Dialling hour profile'
            tooltip='Will determine the hours when leads can be dialled.'
            value={GetLabelFromValue(diallingHourProfileOptions, leadEngineSettings.diallingHourProfile)}
          />
          <DataItem
            stacked={isExtraSmall}
            title='Duplicate search days'
            tooltip='The amount of days for the lead engine to reference to consider a leads as a duplicate.'
            value={leadEngineSettings.duplicateSearchDays}
          />
          <DataItem
            stacked={isExtraSmall}
            title='Endpoint attempt interval seconds'
            tooltip='The number of seconds the lead engine needs to wait before subsequent redials to an endpoint.'
            value={leadEngineSettings.endpointAttemptIntervalSeconds}
          />
          <DataItem
            stacked={isExtraSmall}
            title='Endpoint machine interval seconds'
            tooltip='The number of seconds the lead engine needs to wait before subsequent redials to an endpoint marked with an outcome of answering machine.'
            value={leadEngineSettings.endpointMachineIntervalSeconds}
          />
          <DataItem
            stacked={isExtraSmall}
            title='Endpoint engaged interval seconds'
            tooltip='The number of seconds the lead engine needs to wait before subsequent redials to an endpoint marked with an outcome of engaged.'
            value={leadEngineSettings.endpointEngagedIntervalSeconds}
          />
          <DataItem
            stacked={isExtraSmall}
            title='Endpoint no answer interval seconds'
            tooltip='The number of seconds the lead engine needs to wait before subsequent redials to an endpoint marked with an outcome of no answer.'
            value={leadEngineSettings.endpointNoAnswerIntervalSeconds}
          />
          <DataItem
            stacked={isExtraSmall}
            title='Endpoint daily max attempts'
            tooltip='The maximum number of times a day an endpoint can be dialed.'
            value={leadEngineSettings.endpointDailyMaxAttempts}
          />
          <DataItem
            stacked={isExtraSmall}
            title='External ID attempt interval seconds'
            tooltip='The number of seconds the lead engine needs to wait before subsequent redials to an external ID.'
            value={leadEngineSettings.externalIdAttemptIntervalSeconds}
          />
          <DataItem
            stacked={isExtraSmall}
            title='External ID daily max attempts'
            tooltip='The maximum number of times an external ID can be dialled.'
            value={leadEngineSettings.externalIdDailyMaxAttempts}
          />
          <DataItem
            stacked={isExtraSmall}
            title='Lead endpoint total max attempts'
            tooltip='The maximum number of times a day a lead’s endpoint can be dialled.'
            value={leadEngineSettings.leadEndpointTotalMaxAttempts}
          />
          <DataItem
            stacked={isExtraSmall}
            title='Lead total max attempts'
            tooltip='The minimum number of times a day a lead can be dialled, regardless of the amount of endpoints it has.'
            value={leadEngineSettings.leadTotalMaxAttempts}
          />
          <DataItem
            stacked={isExtraSmall}
            tooltip='The maximum number of times to reschedule a missed callback before it must be handled manually or reassigned to another agent.'
            title='Maximum reschedules for a callback'
            value={
              (leadEngineSettings.callbackMaxReschedules || 0) < 0
                ? 'Unlimited'
                : leadEngineSettings.callbackMaxReschedules
            }
          />
          {(leadEngineSettings.callbackMaxReschedules || 0) != 0 && (
            <>
              <DataItem
                stacked={isExtraSmall}
                tooltip='Reschedule a missed callback for this number of seconds in the future.'
                title='Reschedule callback seconds'
                value={leadEngineSettings.callbackRescheduleSeconds || 0}
              />
              <DataItem
                stacked={isExtraSmall}
                tooltip='Whether a callback can be reassigned to another agent if the assigned agent is not present.'
                title='Reassign missed callback to group'
                value={leadEngineSettings.reassignMissedCallbackToGroup ? 'Yes' : 'No'}
              />
            </>
          )}
          <DataItem
            stacked={isExtraSmall}
            tooltip="Shift a lead's expiry timestamp so that a callback can be performed outside of a leads expected expiry window."
            title='Shift callback lead expiry'
            value={leadEngineSettings.callbackShiftLeadExpiry ? 'Yes' : 'No'}
          />
          <DataItem
            stacked={isExtraSmall}
            tooltip='Whether leads with fewer attempts should be tried before those with higher lead priority.'
            title='Sort attempt count before priority'
            value={leadEngineSettings.sortAttemptCountFirst ? 'Yes' : 'No'}
          />
          <DataItem
            stacked={isExtraSmall}
            tooltip='Whether to dial endpoints in a strict sequence.'
            title='Enable Endpoint Sequence'
            value={leadEngineSettings.enableEndpointSequence ? 'Yes' : 'No'}
          />
          <DataItem
            stacked={isExtraSmall}
            title='Wash lists'
            tooltip='External do not call register used to wash leads within campaign lists.'
            value={leadEngineWashListsDisplay}
          />
          <DataItem
            stacked={isExtraSmall}
            title='Exclusion lists'
            tooltip='Internal do not call register used to wash leads within campaign lists.'
            value={leadEngineExclusionListsDisplay}
          />
        </>
      )}

      {isEdit && (
        <form onSubmit={onSubmitFn} noValidate>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <ControlledCheckbox
                name='duplicateSearchAllCampaigns'
                label='Duplicate search all campaigns?'
                control={control}
                disabled={submitting}
              />
            </Grid>

            <Grid item xs={12}>
              <ControlledCheckbox
                name='washEndpointOnly'
                label='Wash endpoint only?'
                control={control}
                disabled={submitting}
              />
            </Grid>

            <Grid item xs={12}>
              <ControlledCheckbox
                name='allowContactedDuplication'
                label='Allow contacted duplication?'
                control={control}
                disabled={submitting}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name='diallingHourProfile'
                control={control}
                rules={{ required: 'dialling hour profile is required.' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    fullWidth
                    variant='outlined'
                    id='diallingHourProfile'
                    label='Dialling hour profile'
                    disabled={submitting}
                    required={true}
                    error={Boolean(errors.diallingHourProfile)}
                    helperText={errors.diallingHourProfile?.message}>
                    {DiallingHourProfileOptionsList}
                  </TextField>
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <ControlledNumberField
                name='duplicateSearchDays'
                control={control}
                rules={{
                  required: 'duplicate search days is required.',
                }}
                label='Duplicate search days'
                disabled={submitting}
                required={true}
                error={Boolean(errors.duplicateSearchDays)}
                helperText={errors.duplicateSearchDays?.message}
              />
            </Grid>

            <Grid item xs={12}>
              <ControlledNumberField
                name='endpointAttemptIntervalSeconds'
                control={control}
                rules={{
                  required: 'endpoint attempt interval seconds is required.',
                }}
                label='Endpoint attempt interval seconds'
                disabled={submitting}
                required={true}
                error={Boolean(errors.endpointAttemptIntervalSeconds)}
                helperText={errors.endpointAttemptIntervalSeconds?.message}
              />
            </Grid>

            <Grid item xs={12}>
              <ControlledNumberField
                name='endpointMachineIntervalSeconds'
                control={control}
                rules={{
                  required: 'endpoint machine interval seconds is required.',
                }}
                label='Endpoint machine interval seconds'
                disabled={submitting}
                required={true}
                error={Boolean(errors.endpointMachineIntervalSeconds)}
                helperText={errors.endpointMachineIntervalSeconds?.message}
              />
            </Grid>

            <Grid item xs={12}>
              <ControlledNumberField
                name='endpointEngagedIntervalSeconds'
                control={control}
                rules={{
                  required: 'endpoint engaged interval seconds is required.',
                }}
                label='Endpoint engaged interval seconds'
                disabled={submitting}
                required={true}
                error={Boolean(errors.endpointEngagedIntervalSeconds)}
                helperText={errors.endpointEngagedIntervalSeconds?.message}
              />
            </Grid>

            <Grid item xs={12}>
              <ControlledNumberField
                name='endpointNoAnswerIntervalSeconds'
                control={control}
                rules={{
                  required: 'endpoint no answer interval seconds is required.',
                }}
                label='Endpoint no answer interval seconds'
                disabled={submitting}
                required={true}
                error={Boolean(errors.endpointNoAnswerIntervalSeconds)}
                helperText={errors.endpointNoAnswerIntervalSeconds?.message}
              />
            </Grid>

            <Grid item xs={12}>
              <ControlledNumberField
                name='endpointDailyMaxAttempts'
                control={control}
                rules={{
                  required: 'endpoint daily max attempts is required.',
                }}
                label='Endpoint daily max attempts'
                disabled={submitting}
                required={true}
                error={Boolean(errors.endpointDailyMaxAttempts)}
                helperText={errors.endpointDailyMaxAttempts?.message}
              />
            </Grid>

            <Grid item xs={12}>
              <ControlledNumberField
                name='externalIdAttemptIntervalSeconds'
                control={control}
                rules={{
                  required: 'external id attempt interval seconds is required.',
                }}
                label='External ID attempt interval seconds'
                disabled={submitting}
                required={true}
                error={Boolean(errors.externalIdAttemptIntervalSeconds)}
                helperText={errors.externalIdAttemptIntervalSeconds?.message}
              />
            </Grid>

            <Grid item xs={12}>
              <ControlledNumberField
                name='externalIdDailyMaxAttempts'
                control={control}
                rules={{
                  required: 'external id daily max attempts is required.',
                }}
                label='External ID daily max attempts'
                disabled={submitting}
                required={true}
                error={Boolean(errors.externalIdDailyMaxAttempts)}
                helperText={errors.externalIdDailyMaxAttempts?.message}
              />
            </Grid>

            <Grid item xs={12}>
              <ControlledNumberField
                name='leadEndpointTotalMaxAttempts'
                control={control}
                rules={{
                  required: 'lead endpoint total max attempts is required.',
                }}
                label='Lead endpoint total max attempts'
                disabled={submitting}
                required={true}
                error={Boolean(errors.leadEndpointTotalMaxAttempts)}
                helperText={errors.leadEndpointTotalMaxAttempts?.message}
              />
            </Grid>

            <Grid item xs={12}>
              <ControlledNumberField
                name='leadTotalMaxAttempts'
                control={control}
                rules={{
                  required: 'lead total max attempts is required.',
                }}
                label='Lead total max attempts'
                disabled={submitting}
                required={true}
                error={Boolean(errors.leadTotalMaxAttempts)}
                helperText={errors.leadTotalMaxAttempts?.message}
              />
            </Grid>

            <Grid item xs={12}>
              <ControlledCheckbox
                name='allowCallbackReschedule'
                label='Reschedule missed callbacks?'
                control={control}
                disabled={submitting}
              />
            </Grid>

            {allowCallbackReschedule && (
              <>
                <Grid item xs={12}>
                  <ControlledCheckbox
                    name='reassignMissedCallbackToGroup'
                    label='Reassign missed callback to another agent?'
                    control={control}
                    disabled={submitting}
                  />
                </Grid>

                <Grid item xs={12}>
                  <ControlledNumberField
                    name='callbackRescheduleSeconds'
                    control={control}
                    rules={{
                      required: 'callback reschedule seconds is required.',
                      validate: (value) => {
                        if (value < CallbackRescheduleSeconds.Min || value > CallbackRescheduleSeconds.Max) {
                          return `reschedule seconds must be between ${CallbackRescheduleSeconds.Min} - ${CallbackRescheduleSeconds.Max}`;
                        }
                        return true;
                      },
                    }}
                    label='Reschedule callback seconds'
                    disabled={submitting}
                    required={true}
                    error={Boolean(errors.callbackRescheduleSeconds)}
                    helperText={errors.callbackRescheduleSeconds?.message}
                  />
                </Grid>

                <Grid item xs={12}>
                  <ControlledCheckbox
                    name='limitCallbackReschedule'
                    label='Limit the number of times a callback is rescheduled?'
                    control={control}
                    disabled={submitting}
                  />
                </Grid>

                {limitCallbackReschedule && (
                  <Grid item xs={12}>
                    <ControlledNumberField
                      name='callbackMaxReschedules'
                      control={control}
                      rules={{
                        required: 'callback max reschedules is required.',
                        validate: (value) => {
                          if (value < CallbackMaxReschedules.Min || value > CallbackMaxReschedules.Max) {
                            return `max reschedules must be between ${CallbackMaxReschedules.Min} - ${CallbackMaxReschedules.Max}`;
                          }
                        },
                      }}
                      label='Maximum reschedules for a callback'
                      disabled={submitting}
                      required={true}
                      error={Boolean(errors.callbackMaxReschedules)}
                      helperText={errors.callbackMaxReschedules?.message}
                    />
                  </Grid>
                )}
              </>
            )}

            <Grid item xs={12}>
              <ControlledCheckbox
                name='callbackShiftLeadExpiry'
                label='Shift callback lead expiry?'
                control={control}
                disabled={submitting}
              />
            </Grid>

            <Grid item xs={12}>
              <ControlledCheckbox
                name='sortAttemptCountFirst'
                label='Sort leads by attempt count before priority?'
                control={control}
                disabled={submitting}
              />
            </Grid>

            <Grid item xs={12}>
              <ControlledCheckbox
                name='enableEndpointSequence'
                label='Dial endpoints in a strict sequence?'
                control={control}
                disabled={submitting}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name='washLists'
                control={control}
                render={({ field }) => (
                  <>
                    <Autocomplete
                      {...field}
                      multiple
                      onChange={(e, data) => field.onChange(data)}
                      fullWidth
                      filterSelectedOptions
                      disabled={submitting}
                      id='washLists'
                      options={washListOptions}
                      isOptionEqualToValue={(option, value) => option.value === value.value}
                      getOptionLabel={(option) => option?.label || ''}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label='Wash Lists'
                          required={false}
                          error={Boolean(errors.washLists)}
                          helperText={errors.washLists?.message}
                          variant='outlined'
                        />
                      )}
                    />
                  </>
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name='exclusionLists'
                control={control}
                render={({ field }) => (
                  <>
                    <Autocomplete
                      {...field}
                      multiple
                      onChange={(e, data) => field.onChange(data)}
                      fullWidth
                      filterSelectedOptions
                      disabled={submitting}
                      id='exclusionLists'
                      options={exclusionLists}
                      isOptionEqualToValue={(option, value) => option.value === value.value}
                      getOptionLabel={(option) => option?.label || ''}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label='Exclusion Lists'
                          required={false}
                          error={Boolean(errors.exclusionLists)}
                          helperText={errors.exclusionLists?.message}
                          variant='outlined'
                        />
                      )}
                    />
                  </>
                )}
              />
            </Grid>

            <Grid sx={{ textAlign: 'right' }} item xs={12}>
              <Button onClick={toggleEdit}>Cancel</Button>

              <LoadingButton
                sx={{ marginLeft: 1 }}
                type='submit'
                variant='contained'
                disableElevation
                disabled={disablePerformAction}
                loading={submitting}
                color='primary'>
                Update
              </LoadingButton>
            </Grid>
          </Grid>
        </form>
      )}
    </SectionCard>
  );
};

export default LeadEngineSettingsForm;
